import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import {
  useSetLoyaltyScanAndPayMutation,
  useSetSelectedScanAndPayPaymentMethodMutation,
} from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useAuthContext } from 'state/auth';
import { usePaymentContext } from 'state/payment';
import { toast } from 'utils/toast';

import { getSortedValidPaymentMethods, hasValidScanAndPayPaymentMethodOption } from '../utils';

import { IUseScanAndPay } from './types';

/**
 *
 * useScanAndPay contains logic for checking &
 * updating TH scan & pay feature for a specific user
 * as well as selected payment method for scan & pay
 *
 */
export const useScanAndPay = (): IUseScanAndPay => {
  const { user, loading: userLoading, refreshCurrentUser } = useAuthContext();
  const { formatMessage } = useIntl();
  const {
    activeCard,
    refetchActiveCard,
    loyaltyDetails,
    loading: loyaltyDetailsLoading,
  } = useLoyaltyActiveCard();
  const { paymentMethods, loading: paymentsLoading } = usePaymentContext();
  const userScanAndPayEnabled = useMemo(() => loyaltyDetails?.scanAndPay ?? false, [
    loyaltyDetails,
  ]);

  const defaultSelectedPaymentMethodId = useMemo(
    () => user?.details.defaultScanAndPayAccountIdentifier ?? '',
    [user]
  );

  const [scanAndPayToggleOn, setScanAndPayToggleOn] = useState(userScanAndPayEnabled);

  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [updatingSelectedPaymentMethod, setUpdatingSelectedPaymentMethod] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
    defaultSelectedPaymentMethodId
  );

  const [
    setLoyaltyScanAndPayMutation,
    { loading: setLoyaltyScanAndPayLoading },
  ] = useSetLoyaltyScanAndPayMutation({
    onCompleted: async () => {
      // refreshes user's selected scan & pay payment method
      await refreshCurrentUser();
      // refreshes user's scan & pay feature status
      await refetchActiveCard();
    },
  });

  const [
    setSelectedScanAndPayPaymentMethodMutation,
    { loading: setSelectedPaymentMethodLoading },
  ] = useSetSelectedScanAndPayPaymentMethodMutation({
    onCompleted: async () => {
      await refreshCurrentUser();
    },
  });

  const updateScanAndPayStatus = useCallback(
    async (newStatus: boolean, defaultPaymentMethodId: string = '') => {
      setUpdatingStatus(true);
      setUpdatingSelectedPaymentMethod(true);
      // optimistically set selected payment method
      setSelectedPaymentMethodId(defaultPaymentMethodId);
      // optimistically update toggle status
      setScanAndPayToggleOn(newStatus);
      const cardId = activeCard?.cardId ?? '';
      return await setLoyaltyScanAndPayMutation({
        variables: { cardId, scanAndPay: newStatus, defaultPaymentMethodId },
      });
    },
    [activeCard, setLoyaltyScanAndPayMutation]
  );

  // if we're done updating status, make sure it is synced
  useEffect(() => {
    if (updatingStatus && userScanAndPayEnabled === scanAndPayToggleOn) {
      setUpdatingStatus(false);
    }
  }, [loyaltyDetailsLoading, scanAndPayToggleOn, updatingStatus, userScanAndPayEnabled]);

  const updateSelectedScanAndPayPaymentMethod = useCallback(
    async (scanAndPayPaymentMethodId: string) => {
      setUpdatingSelectedPaymentMethod(true);
      setSelectedPaymentMethodId(scanAndPayPaymentMethodId);
      await setSelectedScanAndPayPaymentMethodMutation({
        variables: { scanAndPayPaymentMethodId },
      });
      toast.success(formatMessage({ id: 'scanAndPayPaymentMethodUpdated' }));
    },
    [formatMessage, setSelectedScanAndPayPaymentMethodMutation]
  );

  // if we're done updating payment method, make sure it is synced
  useEffect(() => {
    if (
      updatingSelectedPaymentMethod &&
      defaultSelectedPaymentMethodId === selectedPaymentMethodId
    ) {
      setUpdatingSelectedPaymentMethod(false);
    }
  }, [
    defaultSelectedPaymentMethodId,
    selectedPaymentMethodId,
    updatingSelectedPaymentMethod,
    userLoading,
  ]);

  const userHasValidScanAndPayPaymentMethodOption = hasValidScanAndPayPaymentMethodOption(
    paymentMethods
  );

  const { selectedPaymentMethod, nonSelectedPaymentMethods } = useMemo(
    () =>
      getSortedValidPaymentMethods({
        paymentMethods,
        selectedPaymentMethodId,
      }),
    [paymentMethods, selectedPaymentMethodId]
  );

  return {
    loadingStatus: loyaltyDetailsLoading || !loyaltyDetails,
    scanAndPayEnabledForUser: scanAndPayToggleOn,
    updateScanAndPayStatus,
    updatingStatus: updatingStatus || setLoyaltyScanAndPayLoading,

    loadingSelectedPaymentMethod: userLoading || paymentsLoading,
    nonSelectedPaymentMethods,
    selectedPaymentMethodId,
    selectedPaymentMethod,
    updateSelectedScanAndPayPaymentMethod,
    updatingSelectedPaymentMethod: updatingSelectedPaymentMethod || setSelectedPaymentMethodLoading,
    userHasValidScanAndPayPaymentMethodOption,
  };
};
